import type { AuthHelpersAdmin } from './types.js';
import type { Token } from '../../shared/types/auth/AuthRequest.js';

export const useAuthAdminHelpers = (): AuthHelpersAdmin => {
  const setTokenCookies = (token: Token) => {
    console.log('🟢 DÉBUT setTokenCookies avec token:', token);
    
    // 📏 Tailles des tokens (pour confirmation)
    console.log('📏 Taille accessToken:', token.accessToken.length, 'caractères');
    console.log('📏 Taille refreshToken:', token.refreshToken.length, 'caractères');
    
    // 🚀 SOLUTION : accessToken en sessionStorage, refreshToken en cookie
    
    // 1. RefreshToken en cookie (fonctionne car plus court)
    const refreshTokenCookie = useCookie('refreshTokenAdmin', {
      maxAge: 30 * 60, // 30 minutes
      path: '/',
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax'
    });
    refreshTokenCookie.value = token.refreshToken;
    
    // 2. AccessToken en sessionStorage (pas de limite de taille)
    if (process.client) {
      sessionStorage.setItem('tokenAdmin', token.accessToken);
      console.log('✅ AccessToken stocké en sessionStorage');
    }
    
    // 3. Pour compatibilité serveur, créer un cookie avec un flag
    const hasTokenCookie = useCookie('hasTokenAdmin', {
      maxAge: 30 * 60,
      path: '/',
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax'
    });
    hasTokenCookie.value = 'true';
    
    console.log('🟡 Après assignation - refreshTokenAdmin (cookie):', refreshTokenCookie.value?.substring(0, 50) + '...');
    console.log('🟡 Après assignation - hasTokenAdmin (cookie):', hasTokenCookie.value);
    
    if (process.client) {
      console.log('🟡 Après assignation - tokenAdmin (sessionStorage):', sessionStorage.getItem('tokenAdmin')?.substring(0, 50) + '...');
    }
    
    // Debug avec délai
    setTimeout(() => {
      console.log('🔍 Check 1s - refreshTokenAdmin (cookie):', useCookie('refreshTokenAdmin').value ? '✅ Présent' : '❌ Absent');
      console.log('🔍 Check 1s - hasTokenAdmin (cookie):', useCookie('hasTokenAdmin').value);
      
      if (process.client) {
        console.log('🔍 Check 1s - tokenAdmin (sessionStorage):', sessionStorage.getItem('tokenAdmin') ? '✅ Présent' : '❌ Absent');
      }
    }, 1000);
  };

  const setAccountCookies = (customerAccountIdValue: string) => {
    const accountCookie = useCookie('customerAccountAdmin', {
      maxAge: 30 * 60,
      path: '/',
    });
    accountCookie.value = customerAccountIdValue;
  };

  const setStoreCookies = (storeId: string) => {
    const storeCookie = useCookie('storeAdmin', {
      maxAge: 30 * 60,
      path: '/',
    });
    storeCookie.value = storeId;
  };

  const setStoreViewCookies = (storeViewId: string) => {
    const storeViewCookie = useCookie('storeViewAdmin', {
      maxAge: 30 * 60,
      path: '/',
    });
    storeViewCookie.value = storeViewId;
  };

  const setCurrencyCookies = (currency: string) => {
    const currencyCookie = useCookie('currencyAdmin', {
      maxAge: 30 * 60,
      path: '/',
    });
    currencyCookie.value = currency;
  };

  const clearTokenCookies = () => {
    console.log('🗑️ ATTENTION: clearTokenCookies() appelé!');
    console.trace('🗑️ Stack trace de clearTokenCookies:');
    
    // Supprimer le cookie refreshToken
    useCookie('refreshTokenAdmin').value = null;
    useCookie('hasTokenAdmin').value = null;
    
    // Supprimer l'accessToken du sessionStorage
    if (process.client) {
      sessionStorage.removeItem('tokenAdmin');
      console.log('✅ AccessToken supprimé du sessionStorage');
    }
  };

  const ensureAuthenticated = async (): Promise<string> => {
    // Récupérer les tokens via les helpers
    const { getAccessToken, getRefreshToken } = useTokenHelpers();
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    const now = Math.floor(Date.now() / 1000);

    function decodeJwt(token: string): any | null {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        return JSON.parse(atob(parts[1]));
      } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
      }
    }

    // Check if accessToken is valid
    if (accessToken) {
      const decodedJwt = decodeJwt(accessToken);
      if (decodedJwt?.exp && now < decodedJwt.exp) {
        return accessToken; // Token is still valid
      }
    }

    // Try to refresh the token if refreshToken exists
    if (refreshToken) {
      try {
        const { data } = await useFetch<{
          isAuthenticated: boolean;
          token: string;
          refreshToken: string;
        }>('/api/admin/auth/refresh', {
          method: 'POST',
          body: { refreshToken },
        });

        if (data.value?.isAuthenticated && data.value.token && data.value.refreshToken) {
          // Re-stocker les nouveaux tokens
          setTokenCookies({
            accessToken: data.value.token,
            refreshToken: data.value.refreshToken,
            expireAt: 0 // Pas utilisé
          });
          return data.value.token;
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }

    // If refresh fails, reset authentication state
    try {
      const { data } = await useFetch<{ success: boolean }>('/api/admin/auth/reset-config', {
        method: 'POST',
      });

      if (data.value?.success) {
        const toast = useToast();
        toast.add({
          title: 'Error',
          description: 'Authentication required. Please log in again.',
          color: 'error',
        });
      }
    } catch (error) {
      console.error('Error resetting auth config:', error);
    }

    throw new Error('Authentication required. Please log in again.');
  };

  return {
    setTokenCookies,
    setAccountCookies,
    setStoreCookies,
    setStoreViewCookies,
    setCurrencyCookies,
    clearTokenCookies,
    ensureAuthenticated,
  };
};

// 🆕 Export séparé des helpers de récupération de tokens
export const useTokenHelpers = () => {
  const getAccessToken = (): string | null => {
    if (process.client) {
      return sessionStorage.getItem('tokenAdmin');
    }
    return null;
  };

  const getRefreshToken = (): string | null => {
    const refreshCookie = useCookie('refreshTokenAdmin');
    return refreshCookie.value || null;
  };

  return { getAccessToken, getRefreshToken };
};

// 🧪 Export séparé pour les tests
export const useTestCookies = () => {
  const testCookies = () => {
    console.log('🧪 === TEST COOKIES START ===');
    
    // Test 1: Cookie simple avec valeur string
    const simpleCookie = useCookie('testSimple');
    simpleCookie.value = 'hello';
    console.log('🧪 Test 1 - Cookie simple:', simpleCookie.value);
    
    // Test 2: Cookie avec options
    const optionsCookie = useCookie('testOptions', {
      maxAge: 30 * 60,
      path: '/',
    });
    optionsCookie.value = 'world';
    console.log('🧪 Test 2 - Cookie avec options:', optionsCookie.value);
    
    // Test 3: Vérification après délai
    setTimeout(() => {
      console.log('🧪 Test après 1s - simpleCookie:', useCookie('testSimple').value);
      console.log('🧪 Test après 1s - optionsCookie:', useCookie('testOptions').value);
    }, 1000);
    
    console.log('🧪 === TEST COOKIES END ===');
  };
  
  return { testCookies };
};
